import './styles/index.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { measureTextZoom } from '@/utils/textZoom';
import { initSentry } from '@/config/sentry';
import { initAnalytics } from '@/config/analytics';
import ErrorBoundary from '@/components/ErrorBoundary';
import App from './App';

const IS_NATIVE = Capacitor.isNativePlatform();

// ── Native shell marker ──
// Marks the native shell for CSS that cannot rely on capability queries: the
// Android WebView reports itself as hover-capable with a fine pointer, so
// `@media (pointer: fine)` is not a usable proxy for "has a mouse" here.
if (IS_NATIVE) {
  document.documentElement.classList.add('rc-native');
}

// ── Display type vs the system font scale ──
// Divides the WebView's font-scale multiplier back out of display type so a
// large accessibility setting doesn't overflow the hero title. See
// utils/textZoom.ts. Re-measured on resume below, because the multiplier can
// change while the app is backgrounded.
const applyTextZoom = () =>
  document.documentElement.style.setProperty('--rc-text-zoom', String(measureTextZoom()));
applyTextZoom();

// ── Service worker ──
// Web only. In the native shell the bundle is already on disk, so a precaching
// SW buys nothing and costs correctness: it is a second update mechanism
// racing the APK's own, and it kept serving the previous bundle after an
// update. Existing registrations are torn down so devices that already
// installed one recover; if one was actually controlling the page, reload
// once so the fresh assets are picked up in this launch rather than the next.
if (IS_NATIVE) {
  if ('serviceWorker' in navigator) {
    const wasControlled = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.getRegistrations()
      .then(regs => Promise.all(regs.map(r => r.unregister())))
      .then(() => ('caches' in window ? caches.keys().then(ks => Promise.all(ks.map(k => caches.delete(k)))) : null))
      .then(() => { if (wasControlled) window.location.reload(); })
      .catch(() => {});
  }
} else if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`).catch(() => {});
  });
  // vite-plugin-pwa keeps skipWaiting/clientsClaim on for registerType
  // 'autoUpdate' even with injectRegister: null (it tests `== null`, which
  // matches 'auto'). So a new SW takes over this page immediately — without
  // the reload its own registration script used to do, the page would keep
  // running the old JS against a swapped-out precache. One shot only, or a
  // failed activation could loop.
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

// ── Status bar ──
// Force dark status bar icons so they're visible against the app's light cream
// background (#FAFAF9). Style.Light is the plugin's "light background" name —
// it means dark icons.
//
// This has to be re-asserted, not just set once at boot. Any activity that
// takes over the window — the Google sign-in chooser, a Stripe Custom Tab —
// applies its own status bar appearance, and Android does not restore ours
// when the app comes back, leaving white-on-cream icons. setBackgroundColor is
// a no-op from Android 15 on (edge-to-edge is enforced and the deprecated
// window colour is ignored); the cream comes from the app's own background
// painting behind the status bar. Kept for older devices.
if (IS_NATIVE) {
  const applyStatusBarStyle = () => {
    StatusBar.setStyle({ style: Style.Light }).catch(() => {});
    StatusBar.setBackgroundColor({ color: '#FAFAF9' }).catch(() => {});
  };
  applyStatusBarStyle();
  CapApp.addListener('appStateChange', ({ isActive }) => {
    if (!isActive) return;
    applyStatusBarStyle();
    // The activity is normally recreated when the system font size changes
    // (fontScale is absent from the manifest's configChanges), which would
    // re-run this file anyway — but that is a manifest detail this sizing
    // shouldn't silently depend on.
    applyTextZoom();
  });
}

initSentry();
window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason);
});
initAnalytics();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
